import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GlassCard from './GlassCard';
import TokenManager from './TokenManager';
import { extractDocumentText } from '../services/documentExtraction';
import {
  analyseMedicalText,
  MEDICAL_MODEL
} from '../services/openaiMedicalAnalysis';

const {
  FiCheck,
  FiFile,
  FiFileText,
  FiImage,
  FiLayers,
  FiLoader,
  FiZap
} = FiIcons;

const STAGES = [
  ['preparing', 'Preparing document'],
  ['extracting', 'Extracting text'],
  ['reviewing', 'Checking document size'],
  ['analyzing', 'Generating educational explanation'],
  ['complete', 'Analysis complete']
];

const FileProcessor = ({
  file,
  apiKey,
  onResult,
  onError,
  onProcessingChange
}) => {
  const [stage, setStage] = useState('preparing');
  const [extractedText, setExtractedText] = useState('');
  const [tokenInfo, setTokenInfo] = useState(null);
  const [sectionProgress, setSectionProgress] = useState('');
  const [ocrProgress, setOcrProgress] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const processFile = async () => {
      onProcessingChange(true);
      onError(null);

      try {
        if (!apiKey?.startsWith('sk-')) {
          throw new Error('A valid OpenAI API key is required.');
        }

        setStage('extracting');
        const text = await extractDocumentText(file, setOcrProgress);

        if (cancelled) return;

        setExtractedText(text);
        setStage('reviewing');

        const info = TokenManager.checkTokenLimit(text, MEDICAL_MODEL);
        setTokenInfo(info);

        const sections = info.isWithinLimit
          ? [text]
          : TokenManager.splitTextIntoChunks(text);

        setStage('analyzing');
        const explanations = [];

        for (let index = 0; index < sections.length; index += 1) {
          if (cancelled) return;

          setSectionProgress(
            sections.length > 1
              ? `Section ${index + 1} of ${sections.length}`
              : ''
          );

          explanations.push(await analyseMedicalText({
            text: sections[index],
            apiKey,
            sectionNumber: index + 1,
            sectionCount: sections.length
          }));
        }

        if (cancelled) return;

        setStage('complete');
        onResult({
          originalText: text,
          explanation: explanations.join('\n\n---\n\n'),
          fileName: file.name,
          fileType: file.type,
          tokenInfo: info,
          chunksProcessed: sections.length,
          modelUsed: MEDICAL_MODEL
        });
      } catch (error) {
        if (!cancelled) {
          onError(error.message || 'The document could not be processed.');
        }
      } finally {
        if (!cancelled) {
          onProcessingChange(false);
        }
      }
    };

    processFile();

    return () => {
      cancelled = true;
    };
  }, [apiKey, file, onError, onProcessingChange, onResult]);

  const fileIcon = file.type.startsWith('image/')
    ? FiImage
    : file.type === 'application/pdf'
      ? FiFileText
      : FiFile;

  const activeIndex = STAGES.findIndex(([key]) => key === stage);

  return (
    <GlassCard className="p-4 sm:p-6 mb-8" glow>
      <div className="flex items-center mb-6">
        <SafeIcon icon={fileIcon} className="h-8 w-8 text-green-400 mr-3" />
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-white truncate">{file.name}</h3>
          <p className="text-sm text-green-200">
            {(file.size / 1024).toFixed(1)} KB · {file.type}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {STAGES.map(([key, label], index) => {
          const complete = index < activeIndex;
          const active = index === activeIndex;

          return (
            <motion.div
              key={key}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center"
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 ${
                complete
                  ? 'bg-green-500'
                  : active
                    ? 'bg-cyan-500'
                    : 'bg-slate-700'
              }`}>
                <SafeIcon
                  icon={complete ? FiCheck : active ? FiLoader : FiLayers}
                  className={`h-4 w-4 text-white ${active ? 'animate-spin' : ''}`}
                />
              </div>
              <div>
                <p className={active ? 'text-cyan-300' : complete ? 'text-green-300' : 'text-slate-400'}>
                  {label}
                </p>
                {active && stage === 'extracting' && ocrProgress > 0 && (
                  <p className="text-xs text-cyan-200">OCR progress: {ocrProgress}%</p>
                )}
                {active && sectionProgress && (
                  <p className="text-xs text-cyan-200">{sectionProgress}</p>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {tokenInfo && (
        <GlassCard className="p-4 mt-6" hover={false}>
          <div className="flex items-center justify-between gap-4">
            <span className="text-white flex items-center">
              <SafeIcon icon={FiZap} className="mr-2 text-green-400" />
              {MEDICAL_MODEL}
            </span>
            <span className="text-xs text-green-200 text-right">
              {tokenInfo.tokenCount.toLocaleString()} estimated input tokens
            </span>
          </div>
        </GlassCard>
      )}

      {extractedText && (
        <GlassCard className="p-4 mt-6 max-h-36 overflow-y-auto" hover={false}>
          <p className="text-xs text-green-300 mb-2">
            Extracted text preview — check for OCR errors
          </p>
          <pre className="text-xs text-green-100 whitespace-pre-wrap font-mono">
            {extractedText.slice(0, 800)}
            {extractedText.length > 800 ? '…' : ''}
          </pre>
        </GlassCard>
      )}
    </GlassCard>
  );
};

export default FileProcessor;